import React from 'react';
import { List } from 'immutable';
import { useSelector } from 'react-redux';
import {
  Image,
  Container,
  WysiwygField,
  Button,
} from '@uncinc/uncinc-react-kitchen-sink';
import {
  CoreLink,
  Paragraph,
} from '@uncinc/react-drupal-core';

import { useTranslation } from 'react-i18next';

import { format } from 'date-fns';
import SocialShare from '../../../../components/SocialShare';

import {
  SCREEN_MOBILE,
  SCREEN_NORMAL,
  SCREEN_TABLET,
  VACANCY_TYPES,
  OVERVIEW_REDIRECT,
  APPLY_FORM,
} from '../../../../config/constants';

import './style.scss';

const NodeVacancyFull = ({ entity }) => {
  const {
    t,
  } = useTranslation();
  const currentLanguage = useSelector((state) => state.getIn(['ui', 'language']));

  const nid = entity.getIn(['nid', 0, 'value'], '');
  const title = entity.getIn(['title', 0, 'value'], '');
  const body = entity.getIn(['body', 0, 'processed'], '');
  const hero = entity.getIn(['field_image', 0, 'styles'], '');
  const salary = entity.getIn(['field_salary_range', 0, 'entity', 'name', 0, 'value'], '');
  const workingHours = entity.getIn(['field_working_hours', 0, 'entity', 'name', 0, 'value'], '');
  const location = entity.getIn(['field_location', 0, 'entity', 'name', 0, 'value'], '');
  const vacancyType = entity.getIn(['field_vacancy_type', 0, 'entity', 'field_system_name', 0, 'value'], '');
  const isVolunteerType = vacancyType === VACANCY_TYPES.Volunteer;
  const isPaidType = vacancyType === VACANCY_TYPES.Paid || vacancyType === VACANCY_TYPES.Internship;
  const createdDate = entity.getIn(['created', 0, 'value'], '');
  const formattedDate = format(new Date(createdDate), 'd MMMM y');
  const entityOverviewUrl = OVERVIEW_REDIRECT['vacancies'][vacancyType][currentLanguage];
  const applyFormUrl = `${APPLY_FORM[vacancyType][currentLanguage]}?vacancy=${nid}`;

  return (
    <article className={`${isVolunteerType ? 'volunteer' : 'paid'} NodeVacancyFull `}>
      {/* Start of content */}
      <Container>
        <div className="row">
          <div className="col-12 col-lg-7 offset-lg-5 NodeVacancyFull__heroimage">
            <Image
              lazyload
              includeWebP
              alt={title}
              src={{
                [SCREEN_MOBILE]: hero.getIn(['crop_1_1']),
                [SCREEN_TABLET]: hero.getIn(['crop_3_2']),
                [SCREEN_NORMAL]: hero.getIn(['crop_4_3']),
              }}
            />
          </div>
          <div className="col-12 col-md-10 col-lg-6 NodeVacancyFull__titleboxWrapper">
            <div className="NodeVacancyFull__titlebox">
              <div>
                <CoreLink className="link" to={entityOverviewUrl}>
                  <i className="icon icon-arrow-left" /> {t(`vacancy.back_to_overview_labels.${vacancyType}`)}
                </CoreLink>
              </div>
              <div>
                <h1 className="asH2">{title}</h1>
              </div>

              <SocialShare />
            </div>

            <div className="NodeVacancyFull__details">
              <Button
                href={applyFormUrl}
                className="Button--primary"
              >
                <span>{t('vacancy.apply')} <i className="icon icon-arrow-right icon-variant-2" /></span>
              </Button>
              <div className="small">
                {isPaidType && (
                  <div><i className="icon icon-calendar" />
                    {t('posted_on')} {formattedDate}
                  </div>
                )}
                {location && (
                  <div>
                    <i className="icon icon-pin2" />
                    {location}
                  </div>
                )}
                {workingHours && (
                  <div className="time">
                    <i className="icon icon-time" />
                    {workingHours}
                  </div>
                )}
                {salary && (
                  <div>
                    <span className="euro">€</span>
                    <span className="euroText">{salary}</span>
                  </div>
                )}
              </div>

            </div>

          </div>
        </div>
      </Container>
      {/* End of content */}

      <Container>
        <div className="NodeProjectDetail__body">
          <div className="row">
            <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
              <WysiwygField
                property="schema:text"
                html={body}
              />
            </div>
          </div>
        </div>
      </Container>

      {/* Start of paragraphs */}
      {entity.get('field_paragraphs') ? (
        <div className="node--page--paragraphs-wrapper">
          {entity.get('field_paragraphs', List()).map((paragraph) => {
            const id = paragraph.get('target_id');
            return (
              <Paragraph
                key={`paragraph:${id}`}
                id={id}
                entity={paragraph.get('entity')}
              />
            );
          })}
        </div>
      ) : null}
      {/* End of paragraphs */}

      {/* Start of vacancy paragraphs */}
      {entity.get('field_paragraph_vacancy') ? (
        <div className="NodeVacancyFull__vacancy-paragraphs node--page--vacancy-paragraphs-wrapper">
          {entity.get('field_paragraph_vacancy', List()).map((paragraph) => {
            const id = paragraph.get('target_id');
            return (
              <Paragraph
                key={`paragraph:${id}`}
                id={id}
                entity={paragraph.get('entity')}
              />
            );
          })}
        </div>
      ) : null}
      {/* End of vacancy paragraphs */}

      <Container>
        <div className="NodeVacancyFull__apply">
          <div className="row">
            <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
              <Button
                href={applyFormUrl}
                className="Button--primary"
              >
                <span>{t('vacancy.apply')} <i className="icon icon-arrow-right icon-variant-2" /></span>
              </Button>
            </div>
          </div>
        </div>
      </Container>

      {isPaidType && (
        <Container className="NodeVacancyFull__announcement__wrapper">
          <div className="row">
            <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
              <div className="NodeVacancyFull__announcement">
                <p className="bold">
                  {t('vacancy.announcement')}
                </p>
                <CoreLink to="/">
                  <div className="NodeVacancyFull__announcement__logo" />
                </CoreLink>
              </div>
            </div>
          </div>
        </Container>
      )}

      <Container>
        <div className="NodeVacancyFull__social-share">
          <div className="row">
            <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
              <SocialShare title={t('vacancy.social_share_title')} />
            </div>
          </div>
        </div>
      </Container>
    </article>
  );
};

export default NodeVacancyFull;
